import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  SectionTitle,
  toast,
} from "@porchpass/ui";
import { sendPreApproval } from "src/api/preApproval";
import type { EmailRecipient } from "src/api/preApproval/types";
import { useWorkspace } from "../WorkspaceContext";
import { RecipientListSection } from "./RecipientListSection";
import { splitRecipientsByGroup } from "./recipientUtils";

export function SendPreApprovalModal() {
  const {
    losApplicationId,
    isSendModalOpen,
    setSendModalOpen,
    pdfBlobUrl,
    refreshEmailStatus,
    borrower,
    recipients,
  } = useWorkspace();
  const [borrowerRecipients, setBorrowerRecipients] = useState<
    EmailRecipient[]
  >([]);
  const [dealerRecipients, setDealerRecipients] = useState<EmailRecipient[]>(
    [],
  );
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [newBorrowerEmail, setNewBorrowerEmail] = useState("");
  const [newDealerEmail, setNewDealerEmail] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const allRecipients = useMemo(
    () => [...borrowerRecipients, ...dealerRecipients],
    [borrowerRecipients, dealerRecipients],
  );

  useEffect(() => {
    if (!isSendModalOpen) {
      return;
    }
    setValidationError(null);
    setNewBorrowerEmail("");
    setNewDealerEmail("");
    const { borrowerRecipients: borrowers, dealerRecipients: dealers } =
      splitRecipientsByGroup(recipients ?? []);
    setBorrowerRecipients(borrowers);
    setDealerRecipients(dealers);
    setSelectedIds([...borrowers, ...dealers].map((recipient) => recipient.id));
  }, [isSendModalOpen, recipients]);

  const handleSend = async () => {
    if (selectedIds.length === 0) {
      setValidationError("At least one recipient is required.");
      return;
    }
    const selectedRecipients = allRecipients.filter((recipient) =>
      selectedIds.includes(recipient.id),
    );
    if (selectedRecipients.length !== selectedIds.length) {
      setValidationError("One or more selected recipients are invalid.");
      return;
    }
    if (!losApplicationId) {
      setValidationError("Application identifiers are not available yet.");
      return;
    }
    setIsSending(true);
    setValidationError(null);
    try {
      await sendPreApproval(losApplicationId, selectedRecipients);
      toast.success("Pre-approval sent successfully");
      setSendModalOpen(false);
      try {
        await refreshEmailStatus();
      } catch {
        toast.error(
          "The pre-approval was sent, but its latest status could not be refreshed.",
        );
      }
    } catch (error) {
      setValidationError(
        error instanceof Error ? error.message : "Failed to send pre-approval.",
      );
    } finally {
      setIsSending(false);
    }
  };

  const hasRecipients = allRecipients.length > 0;

  return (
    <Dialog open={isSendModalOpen} onOpenChange={setSendModalOpen}>
      <DialogContent className="max-w-[960px] w-[90vw] h-[640px] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b border-[#E4E7EC]">
          <DialogTitle>
            Send Pre-Approval to{" "}
            {borrower?.name ?? `Application ${losApplicationId ?? "unknown"}`}
          </DialogTitle>
        </DialogHeader>

        <DialogBody className="flex-1 flex min-h-0 overflow-hidden p-0">
          <div className="w-1/2 p-6 overflow-y-auto border-r border-[#E4E7EC]">
            <SectionTitle size="md" title="Recipients" className="mb-4" />
            {!hasRecipients && !newBorrowerEmail && !newDealerEmail ? (
              <p className="text-sm text-[#697083] mb-4">
                {(recipients ?? []).length > 0
                  ? "No valid borrower or dealer email recipients were returned."
                  : "No borrower or dealer email recipients were returned."}{" "}
                Regenerate the letter after recipient information is available,
                or add recipients manually below.
              </p>
            ) : null}

            <div className="flex flex-col gap-6">
              <RecipientListSection
                title="Dealers"
                group="dealer"
                recipients={dealerRecipients}
                selectedIds={selectedIds}
                newEmail={newDealerEmail}
                addEmailLabel="Add dealer email"
                addButtonLabel="Add dealer"
                onRecipientsChange={setDealerRecipients}
                onSelectedIdsChange={setSelectedIds}
                onNewEmailChange={setNewDealerEmail}
                onValidationError={setValidationError}
              />

              <div className="border-t border-[#E4E7EC]" />

              <RecipientListSection
                title="Borrowers"
                group="borrower"
                recipients={borrowerRecipients}
                selectedIds={selectedIds}
                newEmail={newBorrowerEmail}
                addEmailLabel="Add borrower email"
                addButtonLabel="Add borrower"
                onRecipientsChange={setBorrowerRecipients}
                onSelectedIdsChange={setSelectedIds}
                onNewEmailChange={setNewBorrowerEmail}
                onValidationError={setValidationError}
              />
            </div>

            {validationError && (
              <p className="text-sm text-[#B91C1C] mt-4">{validationError}</p>
            )}
          </div>

          <div className="w-1/2 flex flex-col bg-[#1F2937]">
            <div className="px-4 py-3 text-white text-sm border-b border-[#374151]">
              Pre-Approval Document Preview
            </div>
            <div className="flex-1 p-4">
              {pdfBlobUrl ? (
                <iframe
                  title="Send modal PDF preview"
                  src={pdfBlobUrl}
                  className="w-full h-full bg-white rounded"
                />
              ) : (
                <div className="flex items-center justify-center h-full px-6 text-center text-sm text-[#D1D5DB]">
                  Document preview unavailable.
                </div>
              )}
            </div>
          </div>
        </DialogBody>

        <DialogFooter className="px-6 py-4 border-t border-[#E4E7EC]">
          <Button
            variant="secondary"
            onClick={() => setSendModalOpen(false)}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleSend();
            }}
            disabled={isSending || selectedIds.length === 0}
          >
            {isSending ? "Sending..." : "Send Pre-Approval"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
